import { Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { Background } from '../../shared/components/background/background';
import { Connect } from '../../shared/components/connect/connect';
import { BlogCard } from '../../shared/components/blog-card/blog-card';
import { BlogContentService } from '../../shared/services/blog-content.service';
import { IBlogPostMeta } from '../../shared/interfaces/blog-post.interface';
import { SeoService } from '../../shared/services/seo.service';

export type ArchiveType = 'category' | 'tag' | 'author';

const PAGE_SIZE = 24;

// Same slugify as build-blog-data.mjs so filter-by-slug works at runtime
// against the source data which is keyed by raw category/tag/author names.
function slugify(name: string): string {
  return String(name).toLowerCase().trim()
    .replace(/&amp;/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

@Component({
  selector: 'app-archive',
  imports: [CommonModule, Background, Connect, BlogCard],
  templateUrl: './archive.html',
  styleUrls: ['../blog/blog.scss'],
})
export class Archive {
  protected file = 'blog';

  private blogContent = inject(BlogContentService);
  private seo = inject(SeoService);
  private route = inject(ActivatedRoute);

  protected type = signal<ArchiveType>(this.route.snapshot.data['type'] || 'category');
  protected slug = signal<string>(this.route.snapshot.paramMap.get('slug') || '');
  protected page = signal(1);

  protected allPosts = toSignal(this.blogContent.getPosts(), { initialValue: [] as IBlogPostMeta[] });

  protected filtered = computed<IBlogPostMeta[]>(() => {
    const t = this.type();
    const s = this.slug();
    return this.allPosts().filter((p) => {
      if (t === 'category') {
        const cats = (p.categories || []).map(slugify);
        const names = (p.category_names || []).map(slugify);
        return cats.includes(s) || names.includes(s);
      }
      if (t === 'tag') {
        return (p.tags || []).map(slugify).includes(s);
      }
      // author
      if (p.author_slug && p.author_slug === s) return true;
      return slugify(p.author || '') === s;
    });
  });

  protected displayName = computed(() => {
    const t = this.type();
    const s = this.slug();
    const sample = this.filtered()[0];
    if (t === 'category') {
      return sample?.category_names?.[0] || sample?.categories?.[0] || s;
    }
    if (t === 'tag') {
      return sample?.tags?.[0] || s;
    }
    return sample?.author || s;
  });

  protected visible = computed(() => this.filtered().slice(0, this.page() * PAGE_SIZE));
  protected hasMore = computed(() => this.visible().length < this.filtered().length);

  constructor() {
    const t = this.type();
    const s = this.slug();
    const label = t === 'category' ? 'Category' : t === 'tag' ? 'Tag' : 'Author';
    const pathPrefix = `/${t}`;
    const title = `${label}: ${s}`;
    this.seo.apply({
      title,
      description: `Posts in the ${label.toLowerCase()} archive "${s}" on SEM Nexus.`,
      canonical: `${pathPrefix}/${s}/`,
      type: 'website',
    });
    this.seo.setBreadcrumbs([
      { name: 'Home', url: '/' },
      { name: 'Blog', url: '/blog' },
      { name: title },
    ]);
    this.seo.setJsonLd('collection-page', {
      '@type': 'CollectionPage',
      '@id': `https://semnexus.com${pathPrefix}/${s}/#collection`,
      url: `https://semnexus.com${pathPrefix}/${s}/`,
      name: title,
      isPartOf: { '@id': 'https://semnexus.com/#website' },
      inLanguage: 'en-US',
    });
  }

  protected loadMore(): void {
    this.page.update((p) => p + 1);
  }
}
